import React, { useState } from 'react';
import { reviewAPI } from '../../api/reviews';
import toast from 'react-hot-toast';
import { StarIcon } from '@heroicons/react/24/solid';

const ReviewForm = ({ bookingId, businessName, onReviewSubmitted, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);
    try {
      await reviewAPI.submit({
        booking_id: bookingId,
        rating,
        comment: comment.trim() || undefined,
        feedback: feedback.trim() || undefined,
      });

      toast.success('Review submitted successfully!');
      onReviewSubmitted();
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(error?.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      return (
        <button
          key={index}
          type="button"
          className="focus:outline-none"
          onMouseEnter={() => setHoverRating(starValue)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => setRating(starValue)}
        >
          <StarIcon
            className={`h-8 w-8 ${
              starValue <= (hoverRating || rating)
                ? 'text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-2">Write a Review</h2>
          <p className="text-gray-600 mb-6">Share your experience with {businessName}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium mb-2">Rating *</label>
              <div className="flex space-x-1">
                {renderStars()}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {rating > 0 && `${rating} star${rating !== 1 ? 's' : ''}`}
              </p>
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium mb-1">Comment</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell others about your experience..."
                className="w-full input min-h-[80px] resize-none"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{comment.length}/500 characters</p>
            </div>

            {/* Feedback */}
            <div>
              <label className="block text-sm font-medium mb-1">Additional Feedback</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Any suggestions for improvement?"
                className="w-full input min-h-[60px] resize-none"
                maxLength={300}
              />
              <p className="text-xs text-gray-500 mt-1">{feedback.length}/300 characters</p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                disabled={loading || rating === 0}
              >
                {loading ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewForm;